// src/components/home/NextStepCard.jsx
//
// Single-card "next setup step" surface for Home. Replaces the older
// multi-card "what to do next" stack with one focused, assistant-coach
// prompt at a time. Renders the SINGLE most-urgent setup action; the
// coach taps the CTA to do it (or "skip for now" to bump to the next
// step in the ladder).
//
// Voice mirrors TeamSmartActions — direct, calm, one move at a time.
// "Next step: Invite your parents." not a checklist of seven boxes.
//
// Priority ladder (first match wins):
//   1. 0 coach teams              → Create your first team
//   2. has team(s), 0 seasons     → Create your first season
//   3. has season, 0 registrations → Open registration to families
//   4. has regs, 0 confirmed/paid  → Send the registration link
//   5. roster ≥ 3, 0 events        → Add your first practice
//   6. has events, 0 fields        → Set up your fields (weather)
//   7. all clear                   → "You're set. Scrimly's watching."
//
// Skips persist via localStorage (scrimly.skipped.<step_id>) so a
// dismissed step disappears for that browser. Skipping step N reveals
// step N+1; the data-driven "done" state still wins (e.g. once you
// actually create a season, the skip flag for step 2 is moot).
//
// Renders NULL for parents-only users (they have no setup to do).
//
// Architecture: this component does its own Firestore reads for the
// focus team (first coach team) — Home doesn't already load season /
// reg / event / field counts and we don't want to bloat Home with
// those subscriptions when only this card needs them.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  collection, query, where, orderBy, limit, onSnapshot, getCountFromServer, getDocs,
  doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  CheckIcon,
  ClipboardDocumentIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const SKIP_PREFIX = 'scrimly.skipped.';

// Read all skip flags out of localStorage. Wrapped in try/catch because
// localStorage can throw in private-browsing modes / strict iframes.
function readSkips() {
  try {
    const out = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(SKIP_PREFIX)) {
        out[key.slice(SKIP_PREFIX.length)] = true;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function setSkip(stepId) {
  try {
    localStorage.setItem(`${SKIP_PREFIX}${stepId}`, '1');
  } catch {
    // best-effort; if storage is locked we just won't remember the skip
  }
}

/**
 * Subscribe to the focus team's setup state. Returns:
 *   { activeSeason, regCount, paidCount, rosterCount, eventCount, fieldCount, loading }
 * Each piece is fetched lazily — registrations / events / fields only
 * load when the prerequisite step is complete (no point counting events
 * if there's no season yet).
 */
function useFocusTeamSetup(teamId) {
  // `undefined` = not loaded yet (so we can tell "still loading" apart from a
  // known empty result). Each effect sets a concrete value (null / 0 / number)
  // once it resolves. This lets the card show ONE skeleton until everything is
  // known, instead of cascading through wrong "next steps" as each query lands.
  const [activeSeason, setActiveSeason] = useState(undefined);
  const [regCount, setRegCount] = useState(undefined);
  const [paidCount, setPaidCount] = useState(undefined);
  const [rosterCount, setRosterCount] = useState(undefined);
  const [eventCount, setEventCount] = useState(undefined);
  const [fieldCount, setFieldCount] = useState(undefined);
  const [activated, setActivated] = useState(undefined);

  // Active season — live subscription so the ladder advances the
  // moment a season is created in another tab.
  useEffect(() => {
    if (!teamId) {
      setActiveSeason(null);
      return undefined;
    }
    const q = query(
      collection(db, 'seasons'),
      where('teamId', '==', teamId),
      // Drafts included since D10: the auto season starts as a draft
      // and step 2 becomes "open it", so the checklist must see it.
      where('status', 'in', ['open', 'draft']),
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setActiveSeason(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
      },
      () => setActiveSeason(null),
    );
    return unsub;
  }, [teamId]);

  // Registrations + paid count for the active season. One-shot getDocs
  // is fine here — registration counts move slowly and the card doesn't
  // need to re-render the moment a parent submits a form.
  useEffect(() => {
    if (!teamId || !activeSeason?.id) {
      setRegCount(null);
      setPaidCount(null);
      return;
    }
    setRegCount(undefined);
    setPaidCount(undefined);
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'registrations'),
          where('teamId', '==', teamId),
          where('seasonId', '==', activeSeason.id),
        ));
        if (cancelled) return;
        let paid = 0;
        snap.forEach((d) => {
          const r = d.data();
          if (['paid', 'confirmed'].includes(r.status)) paid += 1;
        });
        setRegCount(snap.size);
        setPaidCount(paid);
      } catch {
        if (!cancelled) {
          setRegCount(0);
          setPaidCount(0);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [teamId, activeSeason?.id]);

  // Roster count (actual players doc) — used for the events gate.
  useEffect(() => {
    if (!teamId) { setRosterCount(null); return; }
    setRosterCount(undefined);
    let cancelled = false;
    (async () => {
      try {
        // Players live in the team subcollection: teams/{teamId}/players.
        // Pre-fix this queried the top-level /players collection which
        // doesn't exist and triggered a 403 in console on every Home
        // load. Subcollection scope already implies teamId, so no where
        // filter needed.
        const snap = await getCountFromServer(
          collection(db, 'teams', teamId, 'players'),
        );
        if (!cancelled) setRosterCount(snap.data().count);
      } catch {
        if (!cancelled) setRosterCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  // Events (any kind — practice, game, scrimmage). Count is enough.
  //
  // Uses getDocs (not getCountFromServer) because the /games rule does
  // per-document evaluation (`isTeamStaff(resource.data.teamId)` +
  // membership exists() check), and Firestore's runAggregationQuery
  // requires the rule to be statically provable WITHOUT reading docs.
  // getCountFromServer would 403 even though individual document reads
  // succeed. Slightly more bandwidth (one read per event) but the
  // event count for a single team is small and this only fires on
  // first load of Home.
  useEffect(() => {
    if (!teamId) { setEventCount(null); return; }
    setEventCount(undefined);
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'games'), where('teamId', '==', teamId)),
        );
        if (!cancelled) setEventCount(snap.size);
      } catch {
        if (!cancelled) setEventCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  // Fields subcollection on the team doc. The weather-watch system
  // can also work off an event-level fieldAddress string, but for the
  // setup ladder we treat "has at least one Fields entry" as the
  // canonical signal that the coach has done field setup.
  useEffect(() => {
    if (!teamId) { setFieldCount(null); return; }
    setFieldCount(undefined);
    let cancelled = false;
    (async () => {
      try {
        const snap = await getCountFromServer(
          collection(db, 'teams', teamId, 'fields'),
        );
        if (!cancelled) setFieldCount(snap.data().count);
      } catch {
        if (!cancelled) setFieldCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  // Activation — has this coach ever actually sent a message? Two signals,
  // either counts (mirrors the server ladder in onboardingRecovery.js):
  // a composer message flipped to status 'sent', or a broadcast audit doc.
  // Fail-quiet to false: if rules deny a read, the coach sees the checklist
  // instead of the extras ladder, which is the safe direction. The server
  // sweep remains the source of truth for the founder's view.
  useEffect(() => {
    if (!teamId) { setActivated(false); return; }
    setActivated(undefined);
    let cancelled = false;
    (async () => {
      try {
        const sent = await getDocs(query(
          collection(db, 'teams', teamId, 'messages'),
          where('status', '==', 'sent'),
          limit(1),
        ));
        if (!cancelled && !sent.empty) { setActivated(true); return; }
      } catch { /* fall through to broadcasts */ }
      try {
        const bc = await getDocs(query(
          collection(db, 'teams', teamId, 'broadcasts'),
          limit(1),
        ));
        if (!cancelled) setActivated(!bc.empty);
      } catch {
        if (!cancelled) setActivated(false);
      }
    })();
    return () => { cancelled = true; };
  }, [teamId]);

  // Loading until every signal needed to compute the step is known. reg/paid
  // only matter when there's an active season (otherwise the step is decided
  // before those gates). `undefined` means a query hasn't resolved yet.
  const loading =
    activeSeason === undefined ||
    rosterCount === undefined ||
    eventCount === undefined ||
    fieldCount === undefined ||
    activated === undefined ||
    (!!activeSeason && (regCount === undefined || paidCount === undefined));

  return { activeSeason, regCount, paidCount, rosterCount, eventCount, fieldCount, activated, loading };
}

/**
 * Compute the next step from current setup state + skip flags. Returns
 * a step descriptor or null when the user is on the positive empty
 * state (everything done, nothing skipped to surface).
 *
 * Each step:
 *   - id          — stable string used for the localStorage skip key
 *   - title       — bold headline ("Next step: …")
 *   - sub         — supporting one-liner
 *   - ctaLabel    — primary button label
 *   - ctaTo       — Link target
 *   - skippable   — false for "no team yet" (no point skipping that)
 *   - allClear    — true only for the positive empty state
 */
// Post-activation extras-ladder copy. Bilingual since 2026-07-24: the
// pre-activation checklist was translated but this ladder flipped a
// Spanish coach's Home back to English the moment they activated.
const LADDER_STRINGS = {
  en: {
    create_team:  { title: 'Next step: Create your first team', sub: "Name it, set the age group, and you're ready to invite families.", cta: 'Create a team' },
    create_season:{ title: 'Next step: Create your first season', sub: 'A season holds your schedule, registrations, and roster for one term.', cta: 'Create season' },
    open_registration: { title: 'Next step: Open registration to families', sub: 'Share a join link so parents can sign their kids up — no spreadsheets.', cta: 'Open registration' },
    send_registration_link: { title: 'Next step: Send the registration link to your roster', sub: 'Reminders close the gap fast — most families register within 24h of a nudge.', cta: 'Send reminder' },
    add_first_practice: { title: 'Next step: Add your first practice', sub: 'Set the time, location, and Scrimly handles attendance + weather alerts.', cta: 'Add practice' },
    set_up_fields: { title: 'Next step: Set up your fields', sub: 'Saved fields with addresses unlock weather alerts for every practice and game.', cta: 'Set up fields' },
    add_location: { title: 'Next step: Turn on weather alerts', sub: "Add your home city and Scrimly watches the forecast for every practice and game — you hear about bad weather before the parents ask.", cta: 'Add home city' },
    all_clear: { title: "You're set. Scrimly's watching.", sub: 'Want to send your team a quick hello?', cta: 'Open AI composer' },
    skip: 'Or skip for now',
  },
  es: {
    create_team:  { title: 'Siguiente paso: Crea tu primer equipo', sub: 'Ponle nombre, elige la categoría y listo para invitar a las familias.', cta: 'Crear un equipo' },
    create_season:{ title: 'Siguiente paso: Crea tu primera temporada', sub: 'Una temporada guarda tu calendario, inscripciones y plantel por un periodo.', cta: 'Crear temporada' },
    open_registration: { title: 'Siguiente paso: Abre la inscripción a las familias', sub: 'Comparte un enlace para que los padres inscriban a sus hijos — sin hojas de cálculo.', cta: 'Abrir inscripción' },
    send_registration_link: { title: 'Siguiente paso: Envía el enlace de inscripción a tu equipo', sub: 'Los recordatorios funcionan — la mayoría de las familias se inscribe en 24h tras un aviso.', cta: 'Enviar recordatorio' },
    add_first_practice: { title: 'Siguiente paso: Agrega tu primer entrenamiento', sub: 'Pon hora y lugar, y Scrimly se encarga de la asistencia y las alertas del clima.', cta: 'Agregar entrenamiento' },
    set_up_fields: { title: 'Siguiente paso: Configura tus canchas', sub: 'Las canchas guardadas con dirección activan alertas del clima para cada entrenamiento y partido.', cta: 'Configurar canchas' },
    add_location: { title: 'Siguiente paso: Activa las alertas del clima', sub: 'Agrega tu ciudad y Scrimly vigila el pronóstico de cada entrenamiento y partido — te enteras del mal clima antes de que los padres pregunten.', cta: 'Agregar ciudad' },
    all_clear: { title: 'Todo listo. Scrimly está pendiente.', sub: '¿Quieres enviar un saludo rápido a tu equipo?', cta: 'Abrir compositor IA' },
    skip: 'O salta por ahora',
  },
};

function buildStep({
  hasCoachTeams,
  focusTeamId,
  team = null,
  activeSeason,
  regCount,
  paidCount,
  rosterCount,
  eventCount,
  fieldCount,
  skips,
  L = LADDER_STRINGS.en,
}) {
  // Step 1 — no team yet. Not skippable: there's nothing to do without a team.
  if (!hasCoachTeams) {
    return {
      id: 'create_team',
      title: L.create_team.title,
      sub: L.create_team.sub,
      ctaLabel: L.create_team.cta,
      ctaTo: '/teams/create',
      skippable: false,
    };
  }

  // For steps 2-7 we evaluate against the focus team. If we don't yet
  // have one (still hydrating), bail to null — the parent shows a
  // skeleton during the loading window.
  if (!focusTeamId) return null;

  // Step 2 — has team, no active season.
  if (!activeSeason && !skips.create_season) {
    return {
      id: 'create_season',
      title: L.create_season.title,
      sub: L.create_season.sub,
      ctaLabel: L.create_season.cta,
      ctaTo: '/seasons/create',
      skippable: true,
    };
  }

  // Step 3 — has season, registration not yet open OR no registrations exist.
  if (activeSeason && (regCount ?? 0) === 0 && !skips.open_registration) {
    return {
      id: 'open_registration',
      title: L.open_registration.title,
      sub: L.open_registration.sub,
      ctaLabel: L.open_registration.cta,
      ctaTo: focusTeamId
        ? `/seasons/${activeSeason.id}/registrations`
        : '/seasons/create',
      skippable: true,
    };
  }

  // Step 4 — registrations exist but none are confirmed / paid.
  if (
    activeSeason &&
    (regCount ?? 0) > 0 &&
    (paidCount ?? 0) === 0 &&
    !skips.send_registration_link
  ) {
    return {
      id: 'send_registration_link',
      title: L.send_registration_link.title,
      sub: L.send_registration_link.sub,
      ctaLabel: L.send_registration_link.cta,
      ctaTo: focusTeamId
        ? `/teams/${focusTeamId}/messages/new?type=${encodeURIComponent('Reminder')}`
        : null,
      skippable: true,
    };
  }

  // Step 5 — roster is set (≥3 players) but no events on the schedule.
  if (
    (rosterCount ?? 0) >= 3 &&
    (eventCount ?? 0) === 0 &&
    !skips.add_first_practice
  ) {
    return {
      id: 'add_first_practice',
      title: L.add_first_practice.title,
      sub: L.add_first_practice.sub,
      ctaLabel: L.add_first_practice.cta,
      ctaTo: focusTeamId
        ? `/teams/${focusTeamId}/schedule`
        : null,
      skippable: true,
    };
  }

  // Step 5.5 — events on the schedule but no home city: weather alerts
  // are silently dark, and nothing else ever re-surfaces the location
  // the coach skipped in the wizard (2026-07-24 audit — the wizard's
  // "the extras ladder picks it up later" comment was aspirational
  // until this step existed). Benefit-framed: the coach is being sold
  // weather alerts, not asked for an address.
  if (
    (eventCount ?? 0) > 0 &&
    !(team?.city || team?.homeCity) &&
    !skips.add_location
  ) {
    return {
      id: 'add_location',
      title: L.add_location.title,
      sub: L.add_location.sub,
      ctaLabel: L.add_location.cta,
      ctaTo: focusTeamId ? `/teams/${focusTeamId}/setup` : null,
      skippable: true,
    };
  }

  // Step 6 — has events but no fields configured (weather alerts gap).
  if (
    (eventCount ?? 0) > 0 &&
    (fieldCount ?? 0) === 0 &&
    !skips.set_up_fields
  ) {
    return {
      id: 'set_up_fields',
      title: L.set_up_fields.title,
      sub: L.set_up_fields.sub,
      ctaLabel: L.set_up_fields.cta,
      ctaTo: focusTeamId
        ? `/teams/${focusTeamId}/fields`
        : null,
      skippable: true,
    };
  }

  // Positive empty state — nothing pending.
  return {
    id: 'all_clear',
    title: L.all_clear.title,
    sub: L.all_clear.sub,
    ctaLabel: L.all_clear.cta,
    ctaTo: focusTeamId
      ? `/teams/${focusTeamId}/messages/new`
      : '/recap',
    skippable: false,
    allClear: true,
  };
}

/**
 * NextStepCard
 *
 * Props:
 *   - coachTeams: array of teams the user is a coach on (from TeamContext)
 *   - loading: boolean — true while teams are still hydrating
/* ── Getting-started checklist (features/coach-getting-started/) ──────────
 *
 * Pre-activation replacement for the single-prompt card: the four-step
 * activation arc (team → season → link → first message) shown as a visible
 * checklist with progress, the current step expanded with its CTA, and the
 * destination stated in messaging terms. Once the coach is activated (first
 * message/broadcast sent) the card reverts to the single-prompt extras
 * ladder below. Approved against in-session mockups 2026-07-22.
 */
function GettingStartedChecklist({ hasCoachTeams, focusTeamId, setup, skips, onSkip }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [opening, setOpening] = useState(false);

  const s1 = hasCoachTeams;
  // D10: a DRAFT season (the auto-created one) does not complete step 2 —
  // the step becomes a one-tap review-and-open so nothing parent-facing
  // (waiver, open registration, "You're in!" emails) goes live without
  // the coach's eyes on it.
  const s2 = setup.activeSeason?.status === 'open';
  const draftSeason = setup.activeSeason?.status === 'draft' ? setup.activeSeason : null;
  const s3 = (setup.regCount ?? 0) > 0; // observable proxy: a family joined
  const doneCount = [s1, s2, s3].filter(Boolean).length; // s4 done → card gone

  const openSeason = async () => {
    if (!draftSeason?.id || opening) return;
    setOpening(true);
    try {
      await updateDoc(doc(db, 'seasons', draftSeason.id), {
        status: 'open',
        updatedAt: serverTimestamp(),
      });
      toast.success(t('home.gettingStarted.step2Opened'));
    } catch {
      toast.error(t('home.gettingStarted.step2OpenFailed'));
    } finally {
      setOpening(false);
    }
  };

  // First incomplete step, honoring the skip flag on step 3 only.
  let current = !s1 ? 1 : !s2 ? 2 : !s3 ? 3 : 4;
  if (current === 3 && skips.share_link_checklist) current = 4;

  const registerUrl = focusTeamId && setup.activeSeason?.id
    ? `${window.location.origin}/register/${focusTeamId}/${setup.activeSeason.id}`
    : null;

  const copyLink = async () => {
    if (!registerUrl) return;
    try {
      await navigator.clipboard.writeText(registerUrl);
      setCopied(true);
      toast.success(t('home.gettingStarted.copied'));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(registerUrl); // clipboard blocked: show the URL itself
    }
  };

  const DoneRow = ({ label, hint }) => (
    <div className="flex items-center gap-2.5 px-0.5 py-2">
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#b5f03d]">
        <CheckIcon className="h-3.5 w-3.5 text-[#0d1f14]" />
      </span>
      <span className="text-sm text-zinc-400">
        {label}{hint ? <span className="ml-1 text-xs">{hint}</span> : null}
      </span>
    </div>
  );

  const UpcomingRow = ({ n, label, note }) => (
    <div className="flex items-center gap-2.5 px-0.5 py-2">
      <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] border-zinc-300 text-xs text-zinc-400">{n}</span>
      <span className="text-sm text-zinc-500">{label}</span>
      {note ? <span className="ml-auto text-[11px] text-zinc-400">{note}</span> : null}
    </div>
  );

  const CurrentRow = ({ n, label, sub, cta, skippable }) => (
    <div className="my-1.5 rounded-xl border border-[#b5f03d]/80 bg-[#b5f03d]/10 px-3.5 py-3">
      <div className="flex items-center gap-2.5">
        <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 border-[#94cc28] bg-white text-xs font-semibold text-[#3d6010]">{n}</span>
        <span className="text-[15px] font-semibold text-[#0d1f14]">{label}</span>
      </div>
      {sub && <p className="ml-8 mt-2 mb-2.5 text-[13px] leading-relaxed text-zinc-600">{sub}</p>}
      <div className="ml-8 flex flex-wrap items-center gap-3">
        {cta}
        {skippable && (
          <button
            type="button"
            onClick={onSkip}
            className="text-xs text-zinc-400 underline underline-offset-2 hover:text-zinc-600"
          >
            {t('home.gettingStarted.skip')}
          </button>
        )}
      </div>
    </div>
  );

  const ctaClass = 'inline-flex items-center gap-1.5 rounded-[9px] bg-[#0d1f14] px-3.5 py-2 text-[13px] font-semibold text-[#b5f03d] hover:opacity-90 active:scale-[0.97] transition-all';

  return (
    <div className="mb-6 rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-bold uppercase tracking-tight text-[#0d1f14]" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
          {t('home.gettingStarted.title')}
        </h2>
        <span className="text-xs font-medium text-zinc-500">{t('home.gettingStarted.progress', { done: doneCount })}</span>
      </div>
      <p className="mt-0.5 mb-2.5 text-[13px] leading-snug text-zinc-600">{t('home.gettingStarted.subtitle')}</p>
      <div className="mb-4 flex gap-1">
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={`h-1 flex-1 rounded-full ${i <= doneCount ? 'bg-[#b5f03d]' : 'bg-zinc-200'}`} />
        ))}
      </div>

      {s1
        ? <DoneRow label={t('home.gettingStarted.step1')} />
        : current === 1
          ? <CurrentRow n={1} label={t('home.gettingStarted.step1')} sub={t('home.gettingStarted.step1Sub')}
              cta={<Link to="/teams/create" className={ctaClass}>{t('home.gettingStarted.step1Cta')}<ArrowRightIcon className="h-4 w-4" /></Link>} />
          : <UpcomingRow n={1} label={t('home.gettingStarted.step1')} />}

      {s2
        /* Auto-created seasons (features/auto-default-season/) get the
           "Open your season" label (that's what the coach actually did)
           and a "we set it up for you" hint instead of the fee tip. */
        ? <DoneRow label={setup.activeSeason?.autoCreated
              ? t('home.gettingStarted.step2Open')
              : t('home.gettingStarted.step2')}
            hint={setup.activeSeason?.autoCreated
              ? t('home.gettingStarted.step2AutoHint')
              : t('home.gettingStarted.step2Hint')} />
        : current === 2
          ? draftSeason
            /* D10 one-tap review-and-open: the draft is fully set up;
               the coach either opens it here or reviews details first. */
            ? <CurrentRow n={2} label={t('home.gettingStarted.step2Open')}
                sub={t('home.gettingStarted.step2OpenSub', { name: draftSeason.name || '' })}
                cta={
                  <>
                    <button type="button" onClick={openSeason} disabled={opening}
                      className={ctaClass + (opening ? ' opacity-60' : '')}>
                      {t('home.gettingStarted.step2OpenCta')}<ArrowRightIcon className="h-4 w-4" />
                    </button>
                    <Link to={`/seasons/${draftSeason.id}`}
                      className="text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-700">
                      {t('home.gettingStarted.step2Review')}
                    </Link>
                  </>
                } />
            : <CurrentRow n={2} label={t('home.gettingStarted.step2')} sub={t('home.gettingStarted.step2Sub')}
                cta={<Link to="/seasons/create" className={ctaClass}>{t('home.gettingStarted.step2Cta')}<ArrowRightIcon className="h-4 w-4" /></Link>} />
          /* Preview label before a team exists: the auto-draft flow means
             the coach will OPEN a season, not create one (D10). */
          : <UpcomingRow n={2} label={t('home.gettingStarted.step2Open')} />}

      {s3
        ? <DoneRow label={t('home.gettingStarted.step3')} />
        : current === 3
          ? <CurrentRow n={3} label={t('home.gettingStarted.step3')} sub={t('home.gettingStarted.step3Sub')} skippable
              cta={
                <button type="button" onClick={copyLink} disabled={!registerUrl} className={ctaClass + (registerUrl ? '' : ' opacity-50')}>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  {copied ? t('home.gettingStarted.copied') : t('home.gettingStarted.step3Cta')}
                </button>
              } />
          : <UpcomingRow n={3} label={t('home.gettingStarted.step3')} />}

      {current === 4 && s3
        ? <CurrentRow n={4} label={t('home.gettingStarted.step4')} sub={t('home.gettingStarted.step4Sub')}
            cta={focusTeamId
              ? <Link to={`/teams/${focusTeamId}/messages/new`} className={ctaClass}>{t('home.gettingStarted.step4Cta')}<ArrowRightIcon className="h-4 w-4" /></Link>
              : null} />
        : current === 4 && !s3
          /* Locked-but-current (step 3 skipped or link shared, no family
             yet): the wait must not be a dead end — keep the link one tap
             away so the coach's only job (re-share it) stays actionable. */
          ? <CurrentRow n={4} label={t('home.gettingStarted.step4')} sub={t('home.gettingStarted.step4LockedSub')}
              cta={
                <button type="button" onClick={copyLink} disabled={!registerUrl} className={ctaClass + (registerUrl ? '' : ' opacity-50')}>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  {copied ? t('home.gettingStarted.copied') : t('home.gettingStarted.step4Reshare')}
                </button>
              } />
          : <UpcomingRow n={4} label={t('home.gettingStarted.step4')} note={t('home.gettingStarted.step4Locked')} />}
    </div>
  );
}

/*
 * Renders null for parents-only users (no coachTeams).
 */
export default function NextStepCard({ coachTeams, loading }) {
  const { i18n } = useTranslation();
  const ladderLang = String(i18n?.language || 'en').toLowerCase().startsWith('es') ? 'es' : 'en';
  const L = LADDER_STRINGS[ladderLang];
  // Force a re-render after a skip so the next step shows up without
  // needing a navigation event. We track a "skip bump" counter.
  const [skipBump, setSkipBump] = useState(0);
  const skips = useMemo(() => readSkips(), [skipBump]);

  // Filter out archived teams so a coach who archived their only team
  // still sees "Create your first team." Mirrors Home.jsx's filter.
  const activeCoachTeams = useMemo(() => {
    return (coachTeams || []).filter((t) => {
      const archived = t?.isArchived === undefined ? !!t?.archived : !!t?.isArchived;
      return !archived;
    });
  }, [coachTeams]);

  const hasCoachTeams = activeCoachTeams.length > 0;
  // For multi-team coaches the ladder targets the first team
  // alphabetically (matches Home.jsx's sort) — once they're past the
  // "create a team" step they're past it for every team, so picking
  // any one team to interrogate is fine.
  const focusTeam = activeCoachTeams[0] || null;
  const focusTeamId = focusTeam?.id || null;

  const setup = useFocusTeamSetup(focusTeamId);

  const step = useMemo(
    () =>
      buildStep({
        hasCoachTeams,
        focusTeamId,
        team: focusTeam,
        activeSeason: setup.activeSeason,
        regCount: setup.regCount,
        paidCount: setup.paidCount,
        rosterCount: setup.rosterCount,
        eventCount: setup.eventCount,
        fieldCount: setup.fieldCount,
        skips,
        L,
      }),
    [hasCoachTeams, focusTeamId, focusTeam, setup.activeSeason, setup.regCount,
     setup.paidCount, setup.rosterCount, setup.eventCount, setup.fieldCount, skips, L],
  );

  // Parents-only users: nothing to set up. Render null so the card
  // doesn't take up real estate on their picker.
  if (!loading && !hasCoachTeams && !coachTeams) return null;
  if (!loading && (coachTeams || []).length === 0) {
    // This is the "user has zero coach teams" case. We DO render the
    // create-team step here because the user signed up as a coach and
    // hasn't built one yet. But for pure parents (whose role is
    // 'parent' and who never intend to coach) the step is misleading.
    // We rely on the parent guard at the call site (Home.jsx) to
    // suppress this card for those users.
  }

  // Hold the skeleton until BOTH the teams prop and the focus team's setup
  // signals are fully loaded, then show the final step once — otherwise the
  // card cascades through intermediate "next steps" as each query resolves.
  if (loading || (hasCoachTeams && setup.loading) || !step) {
    return (
      <div className="mb-6 h-32 rounded-2xl bg-zinc-100 animate-pulse" />
    );
  }

  // Pre-activation: the four-step getting-started checklist replaces the
  // single-prompt card entirely. Activation (first sent message/broadcast)
  // flips this off permanently and the extras ladder below takes over.
  if (!setup.activated) {
    return (
      <GettingStartedChecklist
        hasCoachTeams={hasCoachTeams}
        focusTeamId={focusTeamId}
        setup={setup}
        skips={skips}
        onSkip={() => { setSkip('share_link_checklist'); setSkipBump((n) => n + 1); }}
      />
    );
  }

  const handleSkip = () => {
    if (!step.skippable) return;
    setSkip(step.id);
    setSkipBump((n) => n + 1);
  };

  // Positive empty state gets a softer card — check icon, no skip link,
  // ghost CTA. Active steps get the lime/pitch primary CTA.
  const isAllClear = !!step.allClear;

  return (
    <div
      className={
        'mb-6 rounded-2xl border p-5 sm:p-6 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] ' +
        (isAllClear
          ? 'border-[#b5f03d]/40 bg-gradient-to-br from-[#b5f03d]/10 to-white'
          : 'border-zinc-200 bg-white')
      }
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div
          className={
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ' +
            (isAllClear
              ? 'bg-[#b5f03d]/25 text-[#3d6010]'
              : 'bg-[#b5f03d]/15 text-[#5a8a1f]')
          }
        >
          {isAllClear
            ? <CheckCircleIcon className="h-5 w-5" />
            : <SparklesIcon className="h-5 w-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <h2
            className="text-base sm:text-lg font-bold uppercase tracking-tight text-[#0d1f14] leading-tight"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.2px' }}
          >
            {step.title}
          </h2>
          <p className="mt-1 text-sm text-zinc-600 leading-snug">
            {step.sub}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {step.ctaTo ? (
              <Link
                to={step.ctaTo}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                {step.ctaLabel}
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            ) : null}
            {step.skippable && (
              <button
                type="button"
                onClick={handleSkip}
                className="text-xs font-semibold uppercase tracking-[0.6px] text-zinc-500 hover:text-[#0d1f14] underline-offset-2 hover:underline transition-colors"
                style={{ fontFamily: HEAD_FONT }}
              >
                {L.skip}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
